import { runInAction } from 'mobx'
import { getRoomState, getRoomWasGraded } from '../../firestore/RoomState'
import { getRoomStateAIRuns } from '../../firestore/RoomStateAI'
import {
  getRoomStateAnswers,
  getRoomStateAnswersForGroup,
} from '../../firestore/RoomStateAnswer'
import { getRoomStateFeedback } from '../../firestore/RoomStateFeedback'
import { getRoomStateSummary } from '../../firestore/RoomStateSummary'
import { getSection } from '../../firestore/Section'
import { fetchSectionAssignment } from '../../firestore/SectionAssignment'
import { fetchSettingsProcessingVideo } from '../../firestore/SettingsProcessingVideo'
import { getSettingsDefaultProcessingAsset } from '../../firestore/SettingsDefaultProcessingAsset'
import { fetchMedia } from '../../firestore/Media'
import { fetchSlideDeck, getSlideDeck } from '../../firestore/SlideDeck'
import { getPeerReviewQuestionsStream } from '../../firestore/SectionAssignmentPeerReviewQuestion'
import { getUserProfileRoomToken } from '../../firestore/UserProfileRoomToken'
import type { BreakoutUser } from '../../models/BreakoutUser'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { Media } from '../../models/Media'
import type { RoomState } from '../../models/RoomState'
import { RoomStateAnswer } from '../../models/RoomStateAnswer'
import { WrappedFirestoreError } from '../../types'
import type { SlideDeckInclude } from '../../types/slideDeckAccess'
import { populateModelsFromApiResponse } from '../../firestore-mobx/apiModelHelper'
import { SlideModel } from '../../models/SlideModel'
import { SlideDeck } from '../../models/SlideDeck'
import { SlideQuestion } from '../../models/SlideQuestion'
import { SlideDeckExhibit } from '../../models/SlideDeckExhibit'
import { SlideRubric } from '../../models/SlideRubric'
import { SlideDeckRole } from '../../models/SlideDeckRole'
import { SlideCaption } from '../../models/SlideCaption'
import { convertApiDocsToModels } from '../../firestore-mobx/apiModelHelper'
import { parseVtt } from '../../util/vtt'
import { captureException } from '@sentry/core'
import type { MeetingCubit } from '../MeetingCubit'
import { Cubit } from '../core'
import { getRoomStateCollaborationData } from '../../firestore/RoomStateCollaborationData'
import { getSettingsSectionPassPricing } from '../../firestore/SettingsSectionPassPricing'
import { fetchSettingsTermsAndConditions } from '../../firestore/SettingsTermsAndConditons'
import { fetchClickWrap } from '../../firestore/ClickWrap/index.ts'

/**
 * The MeetingDataFetcher class is responsible for fetching data from Firestore
 * and updating the Meeting model with the fetched data.
 *
 * The idea is to break up the Meeting class into smaller, more manageable pieces.
 */
export class MeetingDataFetcher extends Cubit {
  repository: FirebaseRepository
  currentUser: BreakoutUser
  useStreams: boolean = false
  _fetchedSection = false
  private captionsRequestId = 0
  constructor(
    protected meeting: MeetingCubit,
    options: {
      useStreams?: boolean
    } = {}
  ) {
    super()
    this.repository = meeting.repository
    this.currentUser = meeting.currentUser
    this.useStreams = options.useStreams ?? false
  }

  started: boolean = false
  start() {
    if (this.started) {
      console.warn('MeetingDataFetcher already started, skipping start.')
      return
    }
    this.started = true

    this.addStream(
      getRoomState(this.repository, { roomStateId: this.meeting.roomId }),
      async (newRoomState) => {
        if (
          newRoomState.data === undefined &&
          this.meeting.roomState.isLoading
        ) {
          this.meeting.markRoomFailedToLoad()
          return
        }

        if (newRoomState.data === undefined) return

        // this.meeting.sectionId is not set yet, so we get the id from the room state
        const sectionId = newRoomState.data.sectionId

        this.startSectionStream(sectionId)

        await this.meeting.updateRoomState(newRoomState)

        // Gate the membership-restricted subcollection streams. A student who
        // deep-links into a room they don't belong to can read the room_state
        // DOCUMENT but not its subcollections; opening those streams produces
        // permission-denied errors and the loading collections never resolve
        // (infinite spinner). If access is explicitly denied, flag the cubit so
        // the entrypoint can redirect to the assignment details page.
        const canView = this.meeting.currentUserCanViewRoom
        if (canView === false) {
          this.meeting.markRoomAccessDenied()
          return
        }
        // Verdict not known yet (profile still loading); the reaction below
        // re-evaluates and opens the streams once the profile resolves.
        if (canView === undefined) return

        this.getRoomStateDependentData(newRoomState, 'room-state-snapshot')

        this.meeting.onRoomReady()
      },
      {
        onError: (error) => {
          if (
            error instanceof WrappedFirestoreError &&
            this.meeting.roomState.isLoading
          ) {
            // Firestore errored on first load, this means we don't have access
            this.meeting.markRoomFailedToLoad()
          }
        },
      }
    )

    // we only need to subscribe to the group answers once
    this.addGroupAnswersStream()
    this.addLivekitTokenStream()
    this.fetchSettingsProcessingVideo()
    this.fetchDefaultProcessingAsset()
    this.fetchSettingsTermsAndConditions()
    this.addCaptionsReaction()
    this.addRoomAccessReaction()
  }

  // The room access verdict can flip from `undefined` to `true`/`false` after
  // the user profile loads, with no new room_state snapshot to re-trigger the
  // success callback above. This reaction re-evaluates so a legitimately-allowed
  // viewer (e.g. a faculty observer whose profile resolves late) still gets the
  // dependent streams, and a denied user is still flagged for redirect. The
  // dependent-stream openers are idempotent, so re-running them is safe.
  addRoomAccessReaction() {
    this.addReaction({
      whenThisChanges: () => this.meeting.currentUserCanViewRoom,
      thenRunThisCode: (canView) => {
        if (this.meeting.roomState.data === undefined) return
        if (canView === false) {
          this.meeting.markRoomAccessDenied()
        } else if (canView === true) {
          this.getRoomStateDependentData(
            this.meeting.roomState,
            'room-access-verdict'
          )
          this.meeting.onRoomReady()
        }
      },
    })
  }

  startSectionStream(sectionId: string | undefined) {
    if (!sectionId) return

    this.removeStream('section')

    this.addStream(
      getSection(this.repository, { sectionId }),
      (section) => {
        this.meeting.section.replaceModel(section)

        const sectionPassKey = 'section-pass-pricing'

        this.removeStream(sectionPassKey)

        this.addStream(
          getSettingsSectionPassPricing(this.repository),
          (pricing) => {
            this.meeting.sectionPassPricing.replaceModel(pricing)
          },
          {
            name: sectionPassKey,
          }
        )
      },
      {
        name: 'section',
      }
    )
  }

  addCaptionsReaction() {
    this.addReaction({
      whenThisChanges: () => this.meeting.roomState.data.activeSlide,
      thenRunThisCode: () => this.resetCaptions(),
    })
  }

  async resetCaptions({ vttUrl: vttUrlOverride }: { vttUrl?: string } = {}) {
    const requestId = ++this.captionsRequestId
    this.meeting.slideCaptions.replaceModels([])

    const slideId = this.meeting.currentSlideId
    const slide = this.meeting.slideDeckSlides.models.find(
      (slide) => slide.id === slideId
    )
    const vttUrl = vttUrlOverride ?? slide?.data.vttUrl
    if (!slideId || !vttUrl) return

    // Captions come from the slide's signed WebVTT file (getSlideDeckForAssignment).
    try {
      const response = await fetch(vttUrl)
      if (!response.ok) return
      const text = await response.text()
      // A slide change may have raced the fetch — don't clobber the new slide.
      if (
        this.meeting.currentSlideId !== slideId ||
        requestId !== this.captionsRequestId
      )
        return
      const captions = convertApiDocsToModels(
        this.repository,
        parseVtt(text).map((cue, index) => ({
          id: `${slideId}-${index}`,
          data: {
            caption: cue.text,
            startTime: cue.startTime,
            endTime: cue.endTime,
            slideId,
            slideDeckId: this.meeting.slideDeckId,
          },
        })),
        SlideCaption
      )
      this.meeting.slideCaptions.replaceModels(captions)
    } catch (e) {
      // A missing VTT is already handled by the !response.ok return above, so
      // reaching here is an unexpected fetch/parse failure — report it, but
      // still leave captions empty rather than breaking the room.
      captureException(e)
    }
  }

  async getRoomStateDependentData(roomState: RoomState, reason: string) {
    // Fetch slide deck document directly (always allowed, needed for display)
    if (this.useStreams) {
      this.addSlideDeckStream()
    } else {
      // v2 gets the deck from the same Adonis response as its subcollections
      // (see fetchMeetingSlideDeckDataViaApi), so skip the Firestore read there.
      if (
        !(this.repository.readsSlideDeckFromV2 && roomState.data.slideDeckId)
      ) {
        this.fetchSlideDeck(roomState.data.slideDeckId)
      }
      this.fetchAssignment(
        roomState.data.assignmentId,
        roomState.data.sectionId
      )
    }

    // Fetch subcollection data via API (handles authorization server-side)
    this.fetchMeetingSlideDeckDataViaApi(roomState, reason)

    this.addUserAnswersStreams()
    this.addRoomStateFeedbackStream()
    this.addRoomStateSummaryStream()
    this.addRoomStateEngagementStream()
    this.addRoomGradedStream()
    this.addAIRunsStream()
    this.addRoomStateCollaborationDataStream()
    this.addPeerReviewQuestionsStream(roomState)
  }

  addSlideDeckStream() {
    if (this.hasStream('slideDeck')) return
    this.addStream(
      getSlideDeck(this.repository, {
        slideDeckId: this.meeting.slideDeckId,
      }),
      (slideDeck) => {
        // const wasEmpty = this.meeting.slideDeck.isEmpty

        this.meeting.slideDeck.replaceModel(slideDeck)

        // if (!wasEmpty) this.meeting.devModePumpSlide()
      },
      {
        name: 'slideDeck',
      }
    )
  }

  async addLivekitTokenStream() {
    this.addStream(
      getUserProfileRoomToken(this.repository, {
        userId: this.currentUser.uid,
        roomId: this.meeting.roomId,
      }),
      (roomToken) => {
        this.meeting.roomToken.replaceModel(roomToken)
      }
    )
  }

  async fetchSettingsProcessingVideo() {
    const setting = await fetchSettingsProcessingVideo(this.meeting.repository)

    this.meeting.settingsProcessingVideo.replaceModel(setting)
  }

  async fetchDefaultProcessingAsset() {
    try {
      this.addStream(
        getSettingsDefaultProcessingAsset(this.repository),
        (setting) => {
          this.meeting.defaultProcessingAsset.replaceModel(setting)

          if (setting.data === undefined) return

          // If a mediaId is set, fetch the corresponding Media document
          const mediaId = setting.data.mediaId
          if (mediaId && typeof mediaId === 'string' && mediaId !== '') {
            fetchMedia(this.meeting.repository, {
              mediaId: mediaId,
            })
              .then((media) => {
                this.meeting.defaultProcessingMedia.replaceModel(media)
              })
              .catch((error) => {
                console.error('Error fetching default processing media:', error)
                // Leave defaultProcessingMedia in its current state - will fall back to legacy
              })
          } else {
            // Clear the media if mediaId is empty/unset to prevent stale data
            this.meeting.defaultProcessingMedia.replaceModel(
              Media.empty(this.repository)
            )
          }
        },
        {
          name: 'settingsDefaultProcessingAsset',
        }
      )
    } catch (error) {
      console.error('Error fetching default processing asset:', error)
      // If the settings document doesn't exist yet, that's okay - fall back to legacy
      // Silently continue - will use legacy settingsProcessingVideo instead
    }
  }

  async fetchSettingsTermsAndConditions() {
    const setting = await fetchSettingsTermsAndConditions(
      this.meeting.repository
    )
    this.meeting.preMeetingTermsAndConditions.replaceModel(setting)

    if (!setting.data.preMeetingClickWrapId) {
      this.meeting.preMeetingClickWrap.changeLoadingState(false)
      return
    }
    const clickWrap = await fetchClickWrap(this.meeting.repository, {
      clickWrapId: setting.data.preMeetingClickWrapId,
    })
    this.meeting.preMeetingClickWrap.replaceModel(clickWrap)
  }

  addUserAnswersStreams() {
    const userIds = this.meeting.roomState.data.userIds
    // Always stream the current user's answers even if they haven't been
    // added to roomState.userIds yet. A student who reaches /meeting/:roomId
    // by deeplink (bypassing JoinSessionDialog) is not in userIds until they
    // actually join the room — but the pre-work quiz gate at meeting entry
    // needs their answers loaded to know whether to show the interstitial.
    // addUserAnswersStream is idempotent (hasStream guard), so the explicit
    // current-user call is a no-op if their uid is already in userIds.
    this.addUserAnswersStream(this.meeting.currentUser.uid)
    for (const userId of userIds) {
      this.addUserAnswersStream(userId)
    }
  }

  addUserAnswersStream(userId: string) {
    const key = `userAnswers-${userId}`
    // once the stream is added, we don't need to add it again
    // we don't remove user streams as we very rarely (or never) remove users during the meeiting
    if (this.hasStream(key)) return

    this.addStream(
      getRoomStateAnswers(this.repository, {
        roomId: this.meeting.roomId,
        userId: userId,
      }),
      (answers) => {
        runInAction(() => {
          const found = this.meeting.roomStateAnswersPerUser.get(userId)
          if (found) {
            found.replaceModels(answers)
          } else {
            const empty = RoomStateAnswer.emptyCollection(this.repository)
            empty.replaceModels(answers)
            this.meeting.roomStateAnswersPerUser.set(userId, empty)
          }
        })
      },
      { name: key, namespace: 'userAnswers' }
    )
  }

  addGroupAnswersStream() {
    if (this.hasStream('groupAnswers')) return

    this.addStream(
      getRoomStateAnswersForGroup(this.repository, {
        roomId: this.meeting.roomId,
      }),
      (answers) => {
        this.meeting.roomStateAnswersForGroup.replaceModels(answers)
      },
      {
        name: 'groupAnswers',
      }
    )
  }

  addRoomStateFeedbackStream() {
    if (this.hasStream('feedback')) return

    this.addStream(
      getRoomStateFeedback(this.repository, {
        roomId: this.meeting.roomId,
        userId: this.currentUser.uid,
      }),
      (feedback) => {
        this.meeting.roomStateFeedback.replaceModel(feedback)
      },
      {
        name: 'feedback',
      }
    )
  }

  addRoomStateSummaryStream() {
    if (this.hasStream('summary')) return

    this.addStream(
      getRoomStateSummary(this.repository, {
        roomId: this.meeting.roomId,
      }),
      (summary) => {
        this.meeting.roomStateSummary.replaceModel(summary)
      },
      {
        name: 'summary',
      }
    )
  }

  addRoomGradedStream() {
    if (this.hasStream('room-graded')) return

    this.addStream(
      getRoomWasGraded(this.repository, {
        roomStateId: this.meeting.roomId,
      }),
      (roomGraded) => this.meeting.setRoomGraded(roomGraded),
      {
        name: 'room-graded',
        // mark reported so a dead stream doesn't strand meetingResultsState
        onError: () => this.meeting.setRoomGraded(false),
      }
    )
  }

  addAIRunsStream() {
    if (this.hasStream('ai-runs')) return

    this.addStream(
      getRoomStateAIRuns(this.repository, {
        roomStateId: this.meeting.roomId,
      }),
      (aiRuns) => this.meeting.setAIRuns(aiRuns),
      {
        name: 'ai-runs',
        onError: () => this.meeting.setAIRuns([]),
      }
    )
  }

  addRoomStateCollaborationDataStream() {
    if (this.meeting.isPreview) {
      // In preview mode, we don't fetch collaboration data from Firestore,
      // but we need to mark it as loaded (empty) so components can render
      this.meeting.collaborationData.changeLoadingState(false)
      return
    }

    if (this.hasStream('collaborationData')) return

    this.addStream(
      getRoomStateCollaborationData(this.repository, {
        roomId: this.meeting.roomId,
      }),
      (collaborationDatas) => {
        this.meeting.collaborationData.replaceModels(collaborationDatas)
      },
      {
        name: 'collaborationData',
      }
    )
  }

  addRoomStateEngagementStream() {
    if (this.hasStream('engagement')) return
  }

  addPeerReviewQuestionsStream(roomState: RoomState) {
    if (this.hasStream('peerReviewQuestions')) return

    const { sectionId, assignmentId } = roomState.data
    if (!sectionId || !assignmentId) {
      // No assignment, mark as loaded with empty
      this.meeting.peerReviewQuestions.changeLoadingState(false)
      return
    }

    this.addStream(
      getPeerReviewQuestionsStream(this.repository, {
        sectionId,
        assignmentId,
      }),
      (questions) => {
        this.meeting.peerReviewQuestions.replaceModels(questions)
      },
      {
        name: 'peerReviewQuestions',
      }
    )
  }

  private slideDeckFetched?: string
  async fetchSlideDeck(slideDeckId: string) {
    // guard - only fetch the slide deck once
    if (this.slideDeckFetched === slideDeckId) return
    this.slideDeckFetched = slideDeckId

    try {
      const slideDeck = await fetchSlideDeck(this.meeting.repository, {
        slideDeckId: slideDeckId,
      })

      this.meeting.slideDeck.replaceModel(slideDeck)
    } catch (e) {
      // but if we fail, we should try again next time
      this.slideDeckFetched = undefined
    }
  }

  private assignmentFetched?: string
  async fetchAssignment(
    assignmentId: string | undefined,
    sectionId: string | undefined
  ) {
    if (!assignmentId || !sectionId) return
    if (this.assignmentFetched === assignmentId) return
    this.assignmentFetched = assignmentId

    try {
      const assignment = await fetchSectionAssignment(this.meeting.repository, {
        assignmentId,
        sectionId,
      })
      this.meeting.assignment.replaceModel(assignment)
    } catch (e) {
      this.assignmentFetched = undefined
    }
  }

  private meetingApiFetched?: string
  async fetchMeetingSlideDeckDataViaApi(roomState: RoomState, reason: string) {
    const { sectionId, assignmentId, slideDeckId } = roomState.data
    const roomId = roomState.id
    if (this.meetingApiFetched === slideDeckId) return
    this.meetingApiFetched = slideDeckId

    const include: SlideDeckInclude[] = [
      'slides',
      'questions',
      'exhibits',
      'roles',
      'rubrics',
    ]

    try {
      // The repository picks the read source from the global override — we only
      // note whether a v2 read landed so we can populate the deck from that
      // response instead of a separate Firestore read.
      const readFromV2 = this.repository.readsSlideDeckFromV2 && !!slideDeckId

      const result = await this.repository.fetchSlideDeckForAssignment({
        ...(sectionId && { sectionId }),
        ...(assignmentId && { assignmentId }),
        ...(roomId && { roomId }),
        ...(slideDeckId && { slideDeckId }),
        include,
        callsite: 'MeetingDataFetcher.fetchMeetingSlideDeckDataViaApi',
        reason,
      })

      if (!result.success) {
        // Mark collections as loaded (empty) so the meeting page doesn't hang
        runInAction(() => {
          // v2 skips the Firestore deck read, so on a payment denial populate the
          // deck from the response (the endpoint still returns it) — otherwise
          // the deck never loads.
          if (readFromV2 && result.slideDeck) {
            this.meeting.slideDeck.replaceModel(
              SlideDeck.buildModelFromApiResponse(
                this.repository,
                result.slideDeck
              )
            )
            this.slideDeckFetched = slideDeckId
          }
          this.meeting.slideDeckSlides.replaceModels([])
          this.meeting.slideDeckQuestions.replaceModels([])
          this.meeting._slideDeckExhibits.replaceModels([])
          this.meeting.roles.replaceModels([])
          this.meeting.apiRubrics.replaceModels([])
        })
        return
      }

      runInAction(() => {
        // v2 returns the deck in the same response — use it instead of a second
        // Firestore read, and mark the deck fetched so nothing re-reads it.
        if (readFromV2) {
          this.meeting.slideDeck.replaceModel(
            SlideDeck.buildModelFromApiResponse(
              this.repository,
              result.slideDeck
            )
          )
          this.slideDeckFetched = slideDeckId
        }

        populateModelsFromApiResponse(this.repository, result, [
          {
            key: 'slides',
            modelClass: SlideModel,
            target: this.meeting.slideDeckSlides,
          },
          {
            key: 'questions',
            modelClass: SlideQuestion,
            target: this.meeting.slideDeckQuestions,
          },
          {
            key: 'exhibits',
            modelClass: SlideDeckExhibit,
            target: this.meeting._slideDeckExhibits,
          },
          {
            key: 'roles',
            modelClass: SlideDeckRole,
            target: this.meeting.roles,
          },
          {
            key: 'rubrics',
            modelClass: SlideRubric,
            target: this.meeting.apiRubrics,
          },
        ])
      })
    } catch (e) {
      this.meetingApiFetched = undefined
      // Mark collections as loaded (empty) so the meeting page doesn't hang
      runInAction(() => {
        this.meeting.slideDeckSlides.replaceModels([])
        this.meeting.slideDeckQuestions.replaceModels([])
        this.meeting._slideDeckExhibits.replaceModels([])
        this.meeting.roles.replaceModels([])
        this.meeting.apiRubrics.replaceModels([])
      })
      captureException(e, { tags: { feature: 'meeting-slide-deck-api' } })
    }
  }
}
